/**
 * PO Lines Table Component
 * Displays the main data table with headers and rows
 */
import { Card, CardContent } from '../../../ui/card';
import { Tabs, TabsContent } from '../../../ui/tabs';
import { Pagination } from '../../../ui/pagination';
import { Package, ShieldCheck, CheckCheck } from 'lucide-react';
import { Button } from '../../../ui/button';
import { PurchaseOrderLine, ColumnConfig, NextActionResult, POLinesFilters } from './types';
import { featureGatedColumnIds, getSortIcon } from './utils';
import type { User } from '../../../../lib/api';
import { POLineRow } from './POLineRow';
import type { ImportPromiseDatesMethod } from '../ImportPromiseDatesModal';
import { BulkActionBar } from './BulkActionBar';
import { POLinesTableHeader } from './POLinesTableHeader';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { usePortalMode } from '../../../../hooks/usePortalMode';

// CR-3846: Column header configuration for dynamic rendering
interface ColumnHeaderConfig {
  align: 'left' | 'center' | 'right';
  sortable: boolean;
  sortField?: string;
}

// CR-3850: Added additional column headers
/**
 * Alignment and sortability per column id.
 *
 * MUST carry an entry for every id in `DEFAULT_COLUMNS` (types.ts). A column
 * defined there but missing here does not render at all — it appears in the
 * column chooser, ticked, and then simply is not in the table, which is a
 * silent failure rather than a visible one. CR-7468 shipped exactly that and it
 * survived unit tests and typecheck; `everyColumnHasHeaderConfig` in
 * `__tests__/poLineColumns.test.ts` is the pin, because two lists that must
 * agree and are checked by nothing will drift again.
 */
export const COLUMN_HEADER_CONFIG: Record<string, ColumnHeaderConfig> = {
  acknowledge_status: { align: 'left', sortable: true, sortField: 'acknowledge_status' },
  action: { align: 'center', sortable: false },
  is_critical: { align: 'center', sortable: true, sortField: 'is_critical' },
  on_hold: { align: 'center', sortable: true, sortField: 'on_hold' },
  hold_reason: { align: 'left', sortable: true, sortField: 'hold_reason' },
  next_action: { align: 'center', sortable: true, sortField: 'next_action' },
  // CR-6510: not sortable — the value is derived per row from the line's
  // concessions, not a column the database can order by.
  concession_status: { align: 'center', sortable: false },
  po_number: { align: 'left', sortable: true, sortField: 'po_number' },
  line_number: { align: 'center', sortable: true, sortField: 'line_number' },
  erp_order_type: { align: 'left', sortable: false },
  customer_name: { align: 'left', sortable: true, sortField: 'customer_name' },
  supplier_name: { align: 'left', sortable: true, sortField: 'supplier_name' },
  supplier_group_name: { align: 'left', sortable: true, sortField: 'supplier_group_name' },
  part_number: { align: 'left', sortable: true, sortField: 'part_number' },
  part_description: { align: 'left', sortable: false },
  description: { align: 'left', sortable: true, sortField: 'description' },
  quantity: { align: 'right', sortable: true, sortField: 'quantity' },
  // CR-7417: Qty Accepted shows the accepted quantity, which is derived
  // rather than stored, and sorting the whole set by it measured 23.9s against
  // a 0.8-2.3s baseline. Not sortable until the figure is materialised on the
  // row. The alternative — leaving the sort on `received_qty` — would order the
  // grid by a number that is no longer the one displayed; it matched before
  // this diff moved the display, and would not after.
  received_qty: { align: 'right', sortable: false },
  // CR-7416: centred like the other badge columns; not sortable (see types.ts).
  otif_verdict: { align: 'center', sortable: false },
  delivered_warehouse_qty: { align: 'right', sortable: false },
  // CR-7163 (a): derived client-side, so there is nothing for the server to
  // order by — matches pct_delivered's alignment, not its sortability.
  warehouse_fulfilment_pct: { align: 'center', sortable: false },
  // CR-7743: worked out per page; Days Left sorts by the promise date it counts to.
  remaining_to_produce_qty: { align: 'right', sortable: false },
  days_left_to_produce: { align: 'center', sortable: true, sortField: 'days_left_to_produce' },
  // CR-7468: derived per page from the receipts and the NCRs, so there is
  // nothing for the server to order by — same reason as the two above it.
  delivered_qty: { align: 'right', sortable: false },
  rejected_qty: { align: 'right', sortable: false },
  outstanding_qty: { align: 'right', sortable: true, sortField: 'outstanding_qty' },
  pct_delivered: { align: 'center', sortable: true, sortField: 'pct_delivered' },
  unit_price: { align: 'right', sortable: true, sortField: 'unit_price' },
  total_price: { align: 'right', sortable: true, sortField: 'total_price' },
  currency: { align: 'center', sortable: true, sortField: 'currency' },
  delivery_date: { align: 'left', sortable: true, sortField: 'delivery_date' },
  odd_date: { align: 'left', sortable: true, sortField: 'odd_date' },
  supplier_promise_date: { align: 'left', sortable: true, sortField: 'supplier_promise_date' },
  buyer_id: { align: 'left', sortable: true, sortField: 'buyer_id' },
  batch_serial_no: { align: 'left', sortable: true, sortField: 'batch_serial_no' },
  ppv: { align: 'right', sortable: true, sortField: 'ppv' },
  packing_note_number: { align: 'left', sortable: true, sortField: 'packing_note_number' },
  order_date: { align: 'left', sortable: true, sortField: 'order_date' },
  last_received_date: { align: 'left', sortable: true, sortField: 'last_received_date' },
  updated_at: { align: 'left', sortable: true, sortField: 'updated_at' },
  required_date_change_count: { align: 'center', sortable: true, sortField: 'required_date_change_count' },
  promised_date_change_count: { align: 'center', sortable: true, sortField: 'promised_date_change_count' },
  pdf_attached_count: { align: 'center', sortable: true, sortField: 'pdf_attached_count' },
  material_cert_count: { align: 'center', sortable: true, sortField: 'material_cert_count' },
  contract_unit_price: { align: 'right', sortable: true, sortField: 'contract_unit_price' },
  contract_lot_price: { align: 'right', sortable: true, sortField: 'contract_lot_price' },
  drawing_number: { align: 'left', sortable: true, sortField: 'drawing_number' },
  // CR-7333: the column displays drawing_issue with the document version as
  // fallback. The id stays drawing_version so saved layouts survive, and
  // SERVER_SORTABLE translates it to the stored field it sorts on.
  drawing_version: { align: 'left', sortable: true, sortField: 'drawing_version' },
  outstanding_value: { align: 'right', sortable: true, sortField: 'outstanding_value' },
  converted_unit_price: { align: 'right', sortable: true, sortField: 'converted_unit_price' },
  converted_total_value: { align: 'right', sortable: true, sortField: 'converted_total_value' },
  converted_outstanding_value: { align: 'right', sortable: true, sortField: 'converted_outstanding_value' },
  original_promise_date: { align: 'left', sortable: true, sortField: 'original_promise_date' },
  mrp_scheduled_date: { align: 'left', sortable: true, sortField: 'mrp_scheduled_date' },
  priority: { align: 'center', sortable: true, sortField: 'priority' },
  contract_price: { align: 'right', sortable: true, sortField: 'contract_price' },
  exchange_rate: { align: 'right', sortable: true, sortField: 'exchange_rate' },  // CR-4679
  otif_review: { align: 'center', sortable: false },     // CR-7418
};

interface POLinesTableProps {
  lines: PurchaseOrderLine[];
  totalCount: number;
  needsActionCount: number | null;
  /** CR-7418: size of the OTIF Review queue — delivered lines that failed OTIF,
   *  plus any already decided. Counted independently of the active tab. */
  otifReviewCount?: number | null;
  openCount: number | null;
  closedCount: number | null;
  onHoldCount: number | null;
  duePreviousWeeksCount?: number | null;
  dueThisWeekCount?: number | null;
  dueNextWeekCount?: number | null;
  /** True while the batched count request is in flight — badges pulse. */
  countsRefreshing?: boolean;
  /** CR-7418: refetch the list after an OTIF decision, which also re-reads the
   *  review pill's own count from the response. */
  onOtifDone?: () => void;
  /** CR-7418: open the bulk decision dialog for the current selection. */
  onOpenBulkOtif?: (action: 'override' | 'acknowledge') => void;
  /** Quick-search term (part / supplier part / PO). */
  partSearch?: string;
  onQuickPartSearch?: (value: string) => void;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  /** CR-7279: rows the server actually serves per page (the requested size when it honours it). */
  servedPageSize?: number;
  activeTab: string;
  user: User | null;
  columns: ColumnConfig[];
  showColumnDropdown: boolean;
  columnDropdownRef: React.RefObject<HTMLDivElement | null>;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  selectedLineIds: Set<number>;
  bulkAccepting: boolean;
  bulkRejecting: boolean;
  bulkHolding: boolean;
  bulkProposing: boolean;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  onSetActiveTab: (tab: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort: (field: string) => void;
  onToggleColumn: (columnId: string) => void;
  onResetColumns: () => void;
  onSelectAllColumns: () => void;
  onReorderColumns: (dragIndex: number, hoverIndex: number) => void;
  onSetShowColumnDropdown: (show: boolean) => void;
  onClearFilters: () => void;
  onSelectLine: (lineId: number) => void;
  onReviewChanges: (lineId: number) => void;  // CR-4728
  onSelectLineForWorkflow: (line: PurchaseOrderLine, modal: 'accept' | 'decline') => void;
  onProposeChange: (line: PurchaseOrderLine) => void;
  onToggleCritical: (line: PurchaseOrderLine) => void;
  onPutOnHold: (line: PurchaseOrderLine) => void;
  onReleaseHold: (line: PurchaseOrderLine) => void;
  onSelectAll: () => void;
  onToggleSelect: (lineId: number) => void;
  onBulkAccept: (ids: Set<number>, verb: 'accept' | 'acknowledge') => void;
  onBulkReject: (ids: Set<number>) => void;
  onBulkHold: (ids: Set<number>) => void;
  onBulkPropose: (ids: Set<number>) => void;
  onSetSelectedLineIds: (ids: Set<number>) => void;
  /** CR-7778: a customer chooses which upload; a supplier has one. */
  onImportPromiseDates: (method?: ImportPromiseDatesMethod) => void;
  // CR-6679: Order Acknowledgements import (suppliers and customers)
  onImportAcknowledgements?: () => void;
  shouldShowActions: (line: PurchaseOrderLine) => boolean;
  shouldShowDecline: (line: PurchaseOrderLine) => boolean;
  shouldShowProposeChange: (line: PurchaseOrderLine) => boolean;
  getNextAction: (line: PurchaseOrderLine) => NextActionResult;
  getSelectableLines: () => PurchaseOrderLine[];
  canSelectForBulkAccept: (line: PurchaseOrderLine) => boolean;
  filters: POLinesFilters;
}

// Columns hidden in the PO Lines table when schedule_collaboration is enabled
// (these are managed at the schedule level instead).
const SCHEDULE_COLLAB_HIDDEN_TABLE_COLUMNS = new Set([
  'acknowledge_status',
  'action',
  'next_action',
  'on_hold',
  'hold_reason',
  'supplier_promise_date',
]);

export function POLinesTable({
  lines, totalCount, needsActionCount, otifReviewCount = null, openCount, closedCount, onHoldCount,
  duePreviousWeeksCount = null, dueThisWeekCount = null, dueNextWeekCount = null,
  countsRefreshing = false, onOtifDone, onOpenBulkOtif,
  partSearch = '', onQuickPartSearch, currentPage, totalPages, pageSize, servedPageSize,
  activeTab, user, columns, showColumnDropdown,
  columnDropdownRef, sortField, sortDirection, selectedLineIds,
  bulkAccepting, bulkRejecting, bulkHolding, bulkProposing,
  formatDate,
  onSetActiveTab, onPageChange, onPageSizeChange, onSort,
  onToggleColumn, onResetColumns, onSelectAllColumns, onReorderColumns,
  onSetShowColumnDropdown, onClearFilters, onSelectLine, onReviewChanges, onSelectLineForWorkflow,
  onProposeChange, onToggleCritical, onPutOnHold, onReleaseHold,
  onSelectAll, onToggleSelect, onBulkAccept, onBulkReject, onBulkHold, onBulkPropose,
  onSetSelectedLineIds, onImportPromiseDates, onImportAcknowledgements,
  shouldShowActions, shouldShowDecline, shouldShowProposeChange,
  getNextAction, getSelectableLines, canSelectForBulkAccept, filters,
}: POLinesTableProps) {
  // When schedule_collaboration is enabled, the PO Lines tab becomes a read-only
  // order-data view. Strip collab columns from the visible set even if the user
  // had toggled them on previously.
  const { isFeatureEnabled, isOpenOrdersControlHidden, concessionsEnabled } = useMenuSettings();
  // Single source of truth for supplier-ness: portal mode, matching how
  // POLinesFiltersPanel chooses the customer vs supplier filter panel (CR-6347).
  const { isSupplier } = usePortalMode();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  // CR-5884: Open Orders supplier-side hide-toggles (Admin Setup → Purchasing).
  const hideExceptions = isOpenOrdersControlHidden('hide_exceptions', isSupplier);
  const hideActionsColumn = isOpenOrdersControlHidden('hide_actions', isSupplier);
  // CR-6348: a supplier reads the OTIF Review pill and can select nothing on it.
  const hideSelect = scheduleCollabEnabled || (isSupplier && activeTab === 'otif_review');
  let visibleColumns = scheduleCollabEnabled
    ? columns.filter(col => !SCHEDULE_COLLAB_HIDDEN_TABLE_COLUMNS.has(col.id))
    : columns;
  if (hideActionsColumn) {
    visibleColumns = visibleColumns.filter(col => col.id !== 'action');
  }
  // CR-7418 round 7 (Aaron, 19 Sep): on the Closed pill EVERY row is received,
  // and a received line offers no action at all (CR-4697, now including the
  // hold). A column of empty cells is furniture that
  // says nothing, so it goes. Other tabs keep it — they carry a mix.
  //
  // ROUND 3: unless one of the rows on screen is still HELD. A hold outlives
  // the receipt, and round 2's rule is that a held line is always releasable —
  // so dropping the column here was quietly re-creating the trap it fixed, on
  // the one pill where every row qualifies. The column is furniture only when
  // it would genuinely be empty.
  const anyHeldOnPage = lines.some(l => l.on_hold);
  if (activeTab === 'closed' && !anyHeldOnPage) {
    visibleColumns = visibleColumns.filter(col => col.id !== 'action');
  }
  // ERP Order Type is a schedule-line (DE) concept — hide it (table + column
  // picker) for tenants without the po_line_schedules module. CR-8616: the
  // Concession column likewise when no customer in view runs concessions. Gated
  // at render so both react once menu settings finish loading.
  const gatedColumnIds = featureGatedColumnIds(isFeatureEnabled('po_line_schedules'), concessionsEnabled);
  visibleColumns = visibleColumns.filter(col => !gatedColumnIds.has(col.id));
  // CR-7418: the review pill brings its own column with it rather than
  // asking the user to find them in the column picker — AND MOVES THEM NEXT TO
  // THE ACTIONS. Its `order` puts it at 25.9, which is where they
  // belong on every other tab and is useless here: seen on the built page, the
  // Decision column sat past fifteen columns and two horizontal scrolls from
  // the buttons it describes. Spliced positionally rather than by changing
  // `order`, and the saved column state is never written, so leaving the pill
  // puts the grid back exactly as the user had it.
  //
  // CR-7418 round 7 (Aaron, 19 Sep): *"what is the GRN Score Column, what is
  // that even telling me?"* — nothing, on this pill. It is received/ordered as
  // a percentage, which is the gate for whether Override is offered, and the
  // queue's own definition requires received >= ordered, so it read 100% on
  // every row. It is dropped rather than explained. Where the number actually
  // varies is a line that is NOT in this queue, and that is a different ask.
  if (activeTab === 'otif_review') {
    const reviewIds = ['otif_review'];
    const reviewCols = reviewIds
      .map(id => visibleColumns.find(col => col.id === id))
      .filter((col): col is ColumnConfig => col !== undefined)
      .map(col => ({ ...col, visible: true }));
    const rest = visibleColumns.filter(col => !reviewIds.includes(col.id));
    const actionAt = rest.findIndex(col => col.id === 'action');
    const insertAt = actionAt === -1 ? 0 : actionAt + 1;
    visibleColumns = [...rest.slice(0, insertAt), ...reviewCols, ...rest.slice(insertAt)];
    // CR-6348: a supplier takes no action on this pill, so no empty Action column.
    if (isSupplier) visibleColumns = visibleColumns.filter(col => col.id !== 'action');
  }
  // CR-4687 round 3: ≥2 rows selected = bulk mode. Per-line action buttons in
  // each row render disabled while this is true; the only allowed actions are
  // the bulk buttons in the header bar (or clearing the selection).
  const bulkMode = selectedLineIds.size >= 2;
  return (
    <Card data-tour="po-lines-table">
      <Tabs value={activeTab} onValueChange={onSetActiveTab}>
        <POLinesTableHeader
          isSupplier={isSupplier}
          shownCount={lines.length}
          totalCount={totalCount}
          partSearch={partSearch}
          onQuickPartSearch={onQuickPartSearch}
          onImportPromiseDates={onImportPromiseDates}
          onImportAcknowledgements={onImportAcknowledgements}
          columns={columns}
          gatedColumnIds={gatedColumnIds}
          showColumnDropdown={showColumnDropdown}
          columnDropdownRef={columnDropdownRef}
          onSetShowColumnDropdown={onSetShowColumnDropdown}
          onToggleColumn={onToggleColumn}
          onResetColumns={onResetColumns}
          onSelectAllColumns={onSelectAllColumns}
          onReorderColumns={onReorderColumns}
          countsRefreshing={countsRefreshing}
          scheduleCollabEnabled={scheduleCollabEnabled}
          hideExceptions={hideExceptions}
          needsActionCount={needsActionCount}
          otifReviewCount={otifReviewCount}
          duePreviousWeeksCount={duePreviousWeeksCount}
          dueThisWeekCount={dueThisWeekCount}
          dueNextWeekCount={dueNextWeekCount}
          openCount={openCount}
          closedCount={closedCount}
          onHoldCount={onHoldCount}
        />

        <TabsContent value={activeTab} className="m-0">
          <CardContent>
            {lines.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{Object.values(filters).some(v => v) ? 'No lines match your filters' : 'No line items found'}</p>
                {Object.values(filters).some(v => v) && (<button onClick={onClearFilters} className="mt-4 text-sm text-primary hover:underline">Clear filters</button>)}
              </div>
            ) : (
              <>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} servedPerPage={servedPageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} itemLabel="lines" />

                {/* CR-7418: on the review pill the bulk bar is a different bar.
                    The grouped one below splits by acknowledge status, which
                    says nothing here — every line in this queue is `received`
                    and the only batch action is Override. Same bulk plumbing
                    underneath (selection, result log, retry); one button. */}
                {/* CR-7418 round 3 (Aaron, 19 Sep): Override FIRST, then
                    Acknowledge, and both carry their badge's colour so the bar,
                    the row icons, the Decision column and the modal all agree.
                    The bar itself went back to neutral — the red was mine,
                    matching the pill, and a red bar under two coloured buttons
                    makes the buttons the third loudest thing on it. */}
                {/* CR-6518 rework: belt and braces with the hidden tab above —
                    the queue is unreachable for a schedule-grain tenant, but a
                    stale `activeTab` restored from the URL would otherwise
                    rebuild this bar over a selection whose per-line actions the
                    backend now refuses. */}
                {!scheduleCollabEnabled && activeTab === 'otif_review' && selectedLineIds.size > 0 && (
                  /* CR-7463: flex-wrap + justify-between so the selected-count
                     and the action buttons wrap onto a second line at tablet
                     width instead of overflowing off the bar. */
                  <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 rounded-lg border bg-muted/40 px-4 py-2.5 mb-3">
                    <span className="text-sm font-medium">
                      {selectedLineIds.size.toLocaleString()} line{selectedLineIds.size === 1 ? '' : 's'} selected
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => onSetSelectedLineIds(new Set())}>
                        Clear
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => onOpenBulkOtif?.('override')}
                        className="gap-1.5 border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 hover:text-teal-900">
                        <ShieldCheck className="h-4 w-4" />Override
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => onOpenBulkOtif?.('acknowledge')}
                        className="gap-1.5 border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 hover:text-violet-900">
                        <CheckCheck className="h-4 w-4" />Acknowledge
                      </Button>
                    </div>
                  </div>
                )}

                {/* CR-5972: Grouped Bulk Action Bar — splits actions per Ack
                    Status when a mixed selection is made. Hidden when
                    schedule_collaboration is enabled (negotiation moves to
                    the schedule level there). */}
                {!scheduleCollabEnabled && activeTab !== 'otif_review' && (
                  <BulkActionBar
                    selectedLines={lines.filter(l => selectedLineIds.has(l.id))}
                    bulkAccepting={bulkAccepting}
                    bulkRejecting={bulkRejecting}
                    bulkHolding={bulkHolding}
                    bulkProposing={bulkProposing}
                    onClear={() => onSetSelectedLineIds(new Set())}
                    onAccept={onBulkAccept}
                    onReject={onBulkReject}
                    onHold={onBulkHold}
                    onPropose={onBulkPropose}
                  />
                )}

                {/* CR-8196: pb-4 keeps the last row clear of the horizontal
                    scrollbar. A macOS overlay bar takes no space and is drawn
                    over the bottom of the content, which on a short table
                    (one Exceptions line) is the only row. */}
                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] pb-4">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20">
                      <tr className="border-b bg-muted/80 backdrop-blur-sm">
                        {!hideSelect && (
                          /* CR-7463: pin the selection column to the left so the
                             checkboxes stay reachable when the table scrolls
                             horizontally at tablet/narrow widths. bg + z keep
                             the data columns scrolling underneath it. */
                          <th className="text-center py-3 px-2 font-medium sticky left-0 z-30 bg-muted/80 backdrop-blur-sm" style={{ minWidth: '50px' }}>
                            <input type="checkbox" checked={getSelectableLines().length > 0 && selectedLineIds.size === getSelectableLines().length} onChange={onSelectAll} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" title="Select all eligible lines" />
                          </th>
                        )}
                        {visibleColumns.filter(col => col.visible).map(col => {
                          const config = COLUMN_HEADER_CONFIG[col.id];
                          if (!config) return null;
                          const alignClass = config.align === 'center' ? 'text-center' : config.align === 'right' ? 'text-right' : 'text-left';
                          const justifyClass = config.align === 'center' ? 'justify-center' : config.align === 'right' ? 'justify-end' : '';
                          return (
                            <th key={col.id} className={`${alignClass} py-3 px-4 font-medium ${config.sortable ? 'cursor-pointer hover:bg-muted/50' : ''}`} style={{ minWidth: col.minWidth }} onClick={config.sortable && config.sortField ? () => onSort(config.sortField!) : undefined}>
                              {config.sortable ? (<div className={`flex items-center gap-1 ${justifyClass}`}>{col.label} {getSortIcon(config.sortField!, sortField, sortDirection)}</div>) : col.label}
                            </th>
                          );
                        })}
                        <th className="text-center py-3 px-4 font-medium" style={{ minWidth: '80px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <POLineRow
                          key={line.id} line={line} user={user} columns={visibleColumns}
                          hideSelect={hideSelect} bulkMode={bulkMode}
                          otifReview={activeTab === 'otif_review'} onOtifDone={onOtifDone}
                          selectedLineIds={selectedLineIds} formatDate={formatDate}
                          shouldShowActions={shouldShowActions} shouldShowDecline={shouldShowDecline}
                          shouldShowProposeChange={shouldShowProposeChange} getNextAction={getNextAction}
                          canSelectForBulkAccept={canSelectForBulkAccept} onSelectLine={onSelectLine}
                          onReviewChanges={onReviewChanges}
                          onSelectLineForWorkflow={onSelectLineForWorkflow} onProposeChange={onProposeChange}
                          onToggleCritical={onToggleCritical} onPutOnHold={onPutOnHold}
                          onReleaseHold={onReleaseHold} onToggleSelect={onToggleSelect}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} servedPerPage={servedPageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} itemLabel="lines" />
              </>
            )}
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
